import { useParams } from "wouter";
import type { ComponentType } from "react";
import NotFound from "@/pages/NotFound";
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from "@/lib/i18n/locales";

/**
 * Wraps a page component for a locale-prefixed route. Wouter's matcher
 * can't constrain :locale to a specific set of values at the route level
 * (see App.tsx comment), so this validates it here instead -- anything
 * that isn't one of the real supported non-default locales 404s, rather
 * than silently rendering the English page under a nonsense URL like
 * /xyz.
 */
export function withLocaleGuard<P extends object>(Component: ComponentType<P>) {
  return function LocaleGuarded(props: P) {
    const { locale } = useParams<{ locale: string }>();
    const isValid =
      locale &&
      (SUPPORTED_LOCALES as readonly string[]).includes(locale) &&
      locale !== DEFAULT_LOCALE;

    if (!isValid) return <NotFound />;
    return <Component {...props} />;
  };
}
